<template>
	<v-dialog
		:value="dialog"
		scrollable
		persistent
		transition="dialog-transition"
		:max-width="modal_width">

		<div v-shortkey="['esc']" @shortkey="$emit('close')"></div>

		<DropFileZone
			v-if="dialog && is_batchable"
			v-model="batch.file"
			@droppedFiles="onFileDrop($event)"
			:isActive="dialog && is_batchable">
		</DropFileZone>

		<v-card color="transparent">

			<v-card-title class="pt-2 pb-0 background darken-1">
				<h4 class="headline">
					Lancement de nouveaux jobs
					<br>
					<span class="subheading font-italic">
						Démarrez un nouveau job manuellement ou déposez un fichier de batch
					</span>
				</h4>
				<v-spacer />
				<v-tooltip bottom color="background">
					<template v-slot:activator="{ on }">
						<v-btn
							small depressed
							v-on="on"
							color="background darken-1 green--text text--accent-3"
							class="ma-0 text-none"
							@click="openNewLauncher()">
							<v-icon class="mx-1">arrow_forward</v-icon>
							Nouveau launcher (recommandé)
						</v-btn>
					</template>
					<span>
						Accès anticipé au nouveau launcher en version Beta.
						<br>
						Merci de remonter le moindre bug via un ticket svp.
					</span>
				</v-tooltip>
				<v-btn icon large @click="$emit('close')">
					<v-icon large>close</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-title class="pa-2 background">
				<v-container grid-list-md fluid class="px-2 py-0">

					<v-layout row align-baseline justify-end>
						<v-flex xs2>
						</v-flex>
						<v-flex xs10 class="pb-0">
							<v-btn-toggle
								v-model="jd_type_selected"
								class="background toggler"
								@change="onJdTypeChange()">
								<v-btn
									flat small
									v-for="jd_type in jds_types"
									:key="jd_type.text"
									:title="`${jd_type.count} Workflows de type ${jd_type.text}`"
									class="text-none"
									:style="getTypeBtnStyle(jd_type.value, jd_type.text)"
									:disabled="jds_types_disabled"
									:value="jd_type.value">
									{{ jd_type.text }}
									<v-icon
										small
										class="ml-2"
										v-if="jd_type.value === jd_type_selected &&
										!jds_types_disabled">
										cancel
									</v-icon>
								</v-btn>
							</v-btn-toggle>
						</v-flex>
					</v-layout>

					<v-layout row wrap align-baseline>
						<v-flex xs2 class="text-right pr-2">
							Workflow
						</v-flex>
						<v-flex grow class="pt-0">
							<v-autocomplete
								:loading="loading_jds"
								:disabled="mode === 'batch'"
								class="my-0 py-0 darken-input prepend-label"
								ref="jd_selector"
								clearable
								return-object
								autofocus
								item-text="label"
								open-on-clear
								:items="jds_filtered || []"
								persistent-hint
								v-model="jd_selected"
								@change="onJdSelected($event)"
								:hint="jd_selected_description">
								<template slot="selection" slot-scope="data">
									<v-chip
										class="mr-2 pa-0"
										color="background"
										small
										label>
										#{{ data.item.id }}
									</v-chip>
									{{ data.item.label }}
									<v-chip
										v-if="data.item.archived > 0"
										class="ml-2 pa-0"
										color="background"
										small
										label>
										Archivé
									</v-chip>
								</template>
								<template slot="item" slot-scope="data">
									<v-list-tile-avatar>
										<v-chip label color="background" class="mr-2">
											{{ data.item.id }}
										</v-chip>
									</v-list-tile-avatar>
									<v-list-tile-content>
										<v-list-tile-title>
											{{ data.item.label }}
										</v-list-tile-title>
										<v-list-tile-sub-title class="caption">
											{{ data.item.description || '-' }}
										</v-list-tile-sub-title>
									</v-list-tile-content>
									<v-list-tile-action style="display: contents">
										<span style="min-width: 24px">
											<v-icon
												v-if="data.item.archived"
												title="Archivé"
												color="warning">
												archive
											</v-icon>
										</span>
										<span style="min-width: 24px">
											<v-icon
												v-if="data.item.protected"
												title="Protégé"
												color="success">
												lock
											</v-icon>
										</span>
										<span style="min-width: 24px">
											<v-icon
												v-if="data.item.restricted"
												title="Restreint"
												color="info">
												security
											</v-icon>
										</span>
									</v-list-tile-action>
								</template>
							</v-autocomplete>
						</v-flex>
					</v-layout>

					<v-layout row wrap align-baseline>
						<v-flex xs2 class="text-right pr-0">
							Version
							<v-btn
								small flat icon
								class="ma-0"
								@click="lock_version_selector = !lock_version_selector">
								<v-icon small v-if="lock_version_selector">lock</v-icon>
								<v-icon small v-else>lock_open</v-icon>
							</v-btn>
						</v-flex>
						<v-flex grow>
							<v-select
								:loading="loading_jds || loading_versions"
								:items="jd_versions"
								class="my-0 py-0 darken-input prepend-label"
								dense
								persistent-hint
								:disabled="mode === 'batch' ||
													jd_versions.length === 0 ||
													lock_version_selector"
								return-object
								item-value="version_number"
								v-model="jd_version_selected"
								:hint="jd_version_selected_description"
								@change="resetBatch()">
								<template v-slot:selection="data">
									<span class="pl-3">
										{{ data.item.version_number }}
									</span>
								</template>
								<template slot="item" slot-scope="data">
									<v-list-tile-content>
										<v-list-tile-title>
											{{ data.item.version_number }}
										</v-list-tile-title>
										<v-list-tile-sub-title class="caption">
											{{ data.item.version_description || '-' }}
										</v-list-tile-sub-title>
									</v-list-tile-content>
								</template>
							</v-select>
						</v-flex>
						<v-flex shrink>
							<v-btn
								flat
								color="primary"
								class="ma-0"
								:disabled="!is_batchable ||
													!jd_version_selected ||
													!jd_selected"
								@click="downloadXlsx()">
								<v-icon class="mr-2">cloud_download</v-icon>
								batch
							</v-btn>
						</v-flex>
					</v-layout>
				</v-container>
			</v-card-title>

			<v-card-text v-if="mode ==='form' && jd_version_selected" class="background">

				<component
					:is="form_component.initForm"
					class="mx-3"
					:details="parameters || {}"
					:param="param"
					:debug="false"
					:visible="dialog"
					:wf_data="jd_version_selected"
					@modal_width="modal_width = $event || '860px'"
					@change="forms = $event"
					@maestroFormSent="onMaestroFormSent($event)"
					@maestroFormErrorSending="onMaestroFormErrorSending($event)">
				</component>
			</v-card-text>

			<v-card-text v-if="mode ==='batch'" class="pa-0 background">
				<table class="batch-table">
					<colgroup>
						<col
							v-for="(header, indexHead) in batch.sheet_data_head"
							:key="'headerCol' + indexHead"
							style="width: 150px" />
						<col style="width: 15px" />
					</colgroup>
					<thead>
						<tr>
							<th v-for="(header, indexHead) in batch.sheet_data_head"
								:key="'header' + indexHead">
								<span class="subheading">
									{{ header }}
								</span>
							</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(item, indexItem) in batch.sheet_data"
							:key="'item' + indexItem"
							:class="{
								'success--text': item.meta.valid ,
								'error--text': !item.meta.valid ,
								'warning--text': item.meta.warning
							}">
							<td v-for="(key, indexKey) in Object.keys(item.params)"
								:key="'key' + indexKey">
								<span class="cell-chip">
									{{ item.cells[key] }}
								</span>
								{{ item.params[key] }}
							</td>
							<td class="align-middle">
								<v-checkbox
									color="success"
									multiple
									hide-details
									class="ma-0 pa-0"
									v-model="batch.selected_rows"
									:value="indexItem"
									v-if="item.meta.valid && !item.meta.submitted"
									:disabled="!item.meta.valid" />
								<v-icon
									v-if="item.meta.submitted"
									size="23"
									color="success"
									class="ma-0 pa-0">
									sentiment_satisfied
								</v-icon>
								<v-tooltip v-if="!item.meta.valid" bottom color="error">
									<v-icon
										slot="activator"
										size="23"
										color="error"
										class="ma-0 pa-0">
										error
									</v-icon>
									<span>
										<ul class="mb-0">
											<li
												v-for="(message, index) in item.meta.messages"
												:key="'message' + index">
												{{ message }}
											</li>
										</ul>
									</span>
								</v-tooltip>
							</td>
						</tr>
					</tbody>
				</table>
			</v-card-text>

			<v-card-actions v-if="mode === 'form'" class="background darken-1" align-baseline>
				<TicketForm></TicketForm>
				<v-spacer></v-spacer>
				<v-btn
					v-if="follow_jr"
					flat
					color="yellow accent-3"
					class="text-none"
					@click="follow_jr = !follow_jr">
					<v-icon class="mr-1">star</v-icon>
					Suivre le job
				</v-btn>
				<v-btn
					v-else
					flat
					color="background lighten-5"
					class="text-none"
					@click="follow_jr = !follow_jr">
					<v-icon class="mr-1">star_outline</v-icon>
					Suivre le job
				</v-btn>
				<v-btn
					color="primary color--text"
					class="ml-2"
					:loading="submitting"
					:disabled="invalid"
					@click="launch()">
					Lancer
					<v-icon class="ml-1">play_arrow</v-icon>
				</v-btn>
			</v-card-actions>

			<v-card-actions v-if="mode === 'batch'" class="background darken-1">
				<v-container grid-list-md fluid class="pa-0">
					<v-layout row wrap>
						<v-btn
							flat small
							color="warning"
							class="text-none"
							@click="resetBatch()">
							<v-icon class="mr-1">close</v-icon>
							{{ batch.filename }}
						</v-btn>
						<v-spacer></v-spacer>
						<v-btn
							small
							color="primary"
							@click="submitBatch()"
							:loading="batch.submitting"
							:disabled="batch.selected_rows.length === 0"
							class="color--text elevation-8 text-none">
							<span v-if="batch.selected_rows.length > 0">
								Lancer les {{ batch.selected_rows.length }} jobs
							</span>
							<span v-else>
								Lancer les jobs
							</span>
							<v-icon class="ml-1">play_arrow</v-icon>
						</v-btn>
					</v-layout>

					<v-layout row wrap>
						<v-progress-linear
							active
							color="primary"
							query
							:value="batch.progress">
						</v-progress-linear>
					</v-layout>
				</v-container>

			</v-card-actions>

			<v-card-actions v-if="success" class="background darken-1 py-0 px-1">
				<v-layout row wrap>
					<v-flex grow>
						<v-alert
							type="success"
							class="py-1"
							:value="true"
							v-safe-html="success">
						</v-alert>
					</v-flex>
				</v-layout>
			</v-card-actions>

			<v-card-actions v-if="errors.length"
				class="background darken-1 py-0 px-1">
				<v-container grid-list-xs fluid class="pa-0">
					<v-layout row wrap v-for="(error, index) in errors"
						:key="'error' + index">
						<v-flex grow class="pa-0">
							<v-alert
								color="error"
								class="py-1"
								:value="true"
								v-safe-html="error">
							</v-alert>
						</v-flex>
					</v-layout>
				</v-container>
			</v-card-actions>

			<v-card-actions v-if="warnings.length"
				class="background darken-1 py-0 px-1">
				<v-container grid-list-xs fluid class="pa-0">
					<v-layout row wrap v-for="(warning, index) in warnings"
						:key="'warning' + index">
						<v-flex grow class="pa-0">
							<v-alert
								color="warning"
								class="py-1"
								:value="true"
								v-safe-html="warning">
							</v-alert>
						</v-flex>
					</v-layout>
				</v-container>
			</v-card-actions>

		</v-card>

	</v-dialog>
</template>

<script>
import Batch from '@/pages/maestro/launcher/launcher_xlsx.js'
import DropFileZone from '@/components/drop_file_zone.vue'
import MaestroInitForm from '@/pages/maestro/forms/job_or_task_form.vue'
import MaestroInitFormLagardere from '@/pages/maestro/jobs/launcher/job_launcher_form_lagardere.vue'
import MaestroInitFormDCP from '@/pages/maestro/jobs/launcher/job_launcher_form_dcp.vue'
import MaestroInitFormCheckDCP from '@/pages/maestro/jobs/launcher/job_launcher_form_check_dcp.vue'
import MaestroInitFormDVDBD from '@/pages/maestro/jobs/launcher/job_launcher_form_dvd_bd.vue'
import MaestroInitFormLegacy from '@/pages/maestro/jobs/launcher/job_launcher_form_legacy.vue'
import MaestroInitFormLegacyArchiveContract from '@/pages/maestro/jobs/launcher/job_launcher_form_legacy_archive_contract.vue'
import MaestroInitFormAmazonPackageGlobal from '@/pages/maestro/jobs/launcher/job_launcher_form_amazon_package_global.vue'
import MaestroInitFormSuifileFvodServ from '@/pages/maestro/jobs/launcher/job_launcher_form_suifile_fvodserv.vue'
import MaestroInitFormXboxPackage from '@/pages/maestro/jobs/launcher/job_launcher_form_xbox_package.vue'
import MaestroInitFormGenericVantageJob from '@/pages/maestro/jobs/launcher/job_launcher_form_generic_vantage_job.vue'
import maestroInitFormAudioToolsAudioFiles from '@/pages/maestro/jobs/launcher/job_launcher_form_audiotools_audiofiles.vue'
import TicketForm from '@/pages/about/ticket_form.vue'
import { useJdListLight } from '@/composables/jobdescriptor_list_light.js'
import { toRefs } from '@vue/composition-api'

export default {
	name: 'Launcher',
	props: [ 'dialog', 'jd_id', 'parameters', 'param', 'parent_jr_id' ],
	components: {
		DropFileZone,
		MaestroInitForm,
		MaestroInitFormLagardere,
		MaestroInitFormDCP,
		MaestroInitFormCheckDCP,
		MaestroInitFormDVDBD,
		MaestroInitFormLegacy,
		MaestroInitFormLegacyArchiveContract,
		MaestroInitFormAmazonPackageGlobal,
		MaestroInitFormSuifileFvodServ,
		MaestroInitFormXboxPackage,
		MaestroInitFormGenericVantageJob,
		maestroInitFormAudioToolsAudioFiles,
		TicketForm
	},
	setup (props, { root }) {
		const { param } = toRefs(props)
		let types = root.$commons.deepGet(param, 'value.type', null)
		const jdListLightInfos = useJdListLight(types)
		return {
			jds: jdListLightInfos.jds,
			jds_types: jdListLightInfos.jds_types,
			jds_error: jdListLightInfos.error_message,
			loading_jds: jdListLightInfos.is_loading,
			getJobdescriptorListLight: jdListLightInfos.getJdListLight
		}
	},
	data () {
		return {
			mode: 'form',
			form_component: window.appConfig.maestro_wf['default'],
			modal_width: '860px',
			forms: [],
			batch: {
				file: null,
				submitting: false,
				progress: 0,
				ext_handled: ['.XLSX', '.XLS'],
				sheet_data: [],
				sheet_data_head: [],
				selected_rows: [],
				params: [],
				filename: null
			},
			follow_jr: false,
			loading_versions: false,
			success: '',
			errors: [],
			warnings: [],
			submitting: false,
			jd_versions: [],
			jd_type_selected: null,
			jd_selected: null,
			jd_version_selected: null,
			lock_version_selector: true,
			is_batchable: true
		}
	},
	computed: {
		jd_selected_description () {
			return this.$commons.deepGet(this.jd_selected, 'description', '')
		},
		jd_version_selected_description () {
			let desc = this.$commons.deepGet(
				this.jd_version_selected,
				'version_description', ''
			)
			if (desc === 'Version initiale') return ''
			if (desc.indexOf('with no explicit reason') > -1) return ''
			if (desc.indexOf('sans raison explicite') > -1) return ''
			return desc
		},
		invalid () {
			if (!this.jd_version_selected) return true
			let invalid = false
			this.forms.map(form => {
				if (!form.isValid) invalid = true
			})
			if (this.forms.length === 0) {
				invalid = true
			}
			return invalid
		},
		jds_filtered () {
			if (this.jd_type_selected === null ||
				this.jd_type_selected === undefined) return this.jds
			return this.jds.filter(jd => {
				return jd.typing === this.jd_type_selected
			})
		},
		jds_types_disabled () {
			if (this.jd_selected === null) return false
			if (this.jd_selected === undefined) return false
			return true
		}
	},
	watch: {
		dialog (val) {
			if (val && !this.loading_jds) {
				let types = this.$commons.deepGet(this.param, 'type', null)
				this.getJobdescriptorListLight(types)
			}
			if (!val) {
				if (!this.jd_selected) {
					this.jd_type_selected = null
				}
				this.resetBatch()
			}
		},
		jds (val) {
			if (!val) return
			for (let i = 0; i < val.length; i++) {
				if (val[i].id === this.jd_id) {
					this.jd_selected = val[i]
					this.onJdSelected(val[i])
					break
				}
			}
		}
	},
	methods: {
		openNewLauncher () {
			let msg = { dialog: true, version: 2 }
			if (this.jd_id) msg.jd_id = this.jd_id
			if (this.parameters) msg.parameters = this.parameters
			if (this.param) msg.param = this.param
			if (this.parent_jr_id) {
				msg.parent_jr_id = this.parent_jr_id
				msg.direct_edit = true
			}
			this.$Bus.$emit('launcher', msg)
		},
		onJdTypeChange () {
			let jd_selector = this.$refs.jd_selector
			setTimeout(() => {
				if (!jd_selector.isMenuActive) jd_selector.activateMenu()
				setTimeout(() => {
					if (!jd_selector.isFocused) jd_selector.focus()
				}, 40)
			}, 0)
		},
		onJdSelected (jd) {
			this.resetBatch()
			this.jd_versions = []
			this.is_batchable = true
			this.jd_version_selected = null
			this.forms = []
			this.form_component = window.appConfig.maestro_wf['default']
			this.jd_type_selected = null
			if (!jd) return
			for (let i = 0; i < this.jds_types.length; i++) {
				if (this.jds_types[i].value === jd.typing) {
					this.jd_type_selected = jd.typing
					break
				}
			}
			this.loading_versions = true
			this.$EsMaestro.jobdescriptor_read(jd.id)
				.then(result => {
					this.jd_versions = result
					this.setInitForm(result[0].descriptor_id)
					this.jd_version_selected = result[0]
				})
				.catch(error => {
					console.error(error)
				})
				.finally(() => {
					this.loading_versions = false
				})
		},
		getTypeBtnStyle (type, type_text) {
			let color = this.$colors.stringToHsl(type_text, 70)
			if (type === this.jd_type_selected) {
				return `background-color: ${color}; color: black !important`
			} else {
				return `border-top: 2px solid ${color}`
			}
		},
		setInitForm (jd_id) {
			if (jd_id) {
				for (var key in window.appConfig.maestro_wf) {
					let currentId = window.appConfig.maestro_wf[key].id
					if (currentId) {
						currentId = parseInt(currentId)
						if (currentId === jd_id) {
							let formComponent = window.appConfig.maestro_wf[key]
							let initBatchable = formComponent.initBatchable
							if (initBatchable === undefined) initBatchable = true
							this.is_batchable = initBatchable
							this.form_component = formComponent
							return
						}
					}
				}
			}
			let formComponent = window.appConfig.maestro_wf['default']
			this.is_batchable = true
			this.form_component = formComponent
		},
		onMaestroFormSent (evt) {
			this.success = `Job créé`
			setTimeout(() => {
				this.$emit('close')
				this.forms = []
				// hack to reset specific form
				let jd = this.jd_version_selected
				this.jd_version_selected = null
				this.$nextTick(() => { this.jd_version_selected = jd })
			}, 1000)
		},
		onMaestroFormErrorSending (evt) {
			this.errors.push(evt.statusText)
		},
		async launch () {
			this.submitting = true
			let isOk = true
			let response = null
			this.errors = []
			for (const form of this.forms) {
				let data = {
					jd_id: form.jobdescriptorID,
					jd_version: form.jobDescriptorVersion,
					parameters: form.parameters
				}
				if (this.parent_jr_id) {
					data.parent_jobrequest_id = this.parent_jr_id
				}
				try {
					response = await this.$ApiMaestro.jobrequest_create(data)
					if (this.$commons.checkResponse(response)) {
						this.success = `Job(s) créé(s)`
						if (response.data.created && this.follow_jr) {
							setTimeout(() => {
								this.$Bus.$emit('follow-jrs', [response.data.created])
							}, 2000)
						}
					} else {
						isOk = false
					}
				} catch (error) {
					console.error(error.response)
					if (error.response) {
						this.errors.push(error.response.statusText)
						Object.keys(error.response.data).map(key => {
							error.response.data[key].map(err => {
								this.errors.push(err)
							})
						})
					} else {
						this.errors.push(error.message)
					}
					isOk = false
				}
			}
			this.submitting = false
			setTimeout(() => {
				if (isOk) {
					this.$emit('close')
					this.forms = []
					// hack to reset specific form
					let jd = this.jd_version_selected
					this.jd_version_selected = null
					this.$nextTick(() => { this.jd_version_selected = jd })
				}
			}, 1000)
		},
		downloadXlsx () {
			Batch.download_xlsx(
				this.jd_version_selected,
				this.jd_versions[0].version_number
			)
		},
		onFileDrop (droppedFiles) {
			this.errors = []
			this.warnings = []
			this.success = ''
			this.batch.submitting = false
			this.batch.sheet_data = []
			this.batch.sheet_data_head = []
			this.batch.selected_rows = []
			this.batch.params = []
			this.batch.filename = null
			if (!droppedFiles) return
			if (droppedFiles.length !== 1) return

			let file = droppedFiles[0]

			let ext = file.name.substring(file.name.lastIndexOf('.'))
			if (this.batch.ext_handled.indexOf(ext.toUpperCase()) === -1) {
				this.errors.push('Seulement les fichiers .XLSX or .XLS ne sont gérés')
				return
			}

			let basename = file.name.substring(0, file.name.lastIndexOf('.'))
			if (!basename) {
				this.errors.push('Impossible de determiner le nom du fichier de batch')
				return
			}

			this.batch.filename = file.name

			let basenameArr = basename.split('_')
			let workflowIdSection = basenameArr[2]
			let workflowVersionSection = basenameArr[3]
			let workflowLastSection = basenameArr[4]
			let workflowId = null
			if (workflowIdSection) {
				workflowId = parseInt(workflowIdSection.split('-')[1])
			}
			if (!workflowId) {
				this.errors.push('Impossible de determiner l\'id du workflow')
				return
			}

			let workflowVersion = null
			if (!workflowLastSection && workflowVersionSection) {
				workflowVersion = parseInt(workflowVersionSection.split('-')[1], 10)
			} else if (workflowLastSection.indexOf('LAST') > -1) {
				workflowVersion = 'LAST'
			} else {
				this.errors.push('Impossible de determiner la version du workflow')
				return
			}

			for (let i = 0; i < this.jds.length; i++) {
				if (this.jds[i].id === workflowId) {
					this.jd_selected = this.jds[i]
					break
				}
			}
			this.$EsMaestro.jobdescriptor_read(workflowId)
				.then(result => {
					this.jd_versions = result
					if (workflowVersion === 'LAST') {
						this.jd_version_selected = result[0]
						this.batch.params = result[0].parameters
					} else {
						for (let i = 0; i < result.length; i++) {
							if (result[i].version_number === workflowVersion) {
								this.jd_version_selected = result[i]
								this.batch.params = result[i].parameters
								break
							}
						}
					}
					if (this.batch.params.length === 0) {
						throw new Error('Impossible de retrouver les paramètres du workflow')
					}
					return Batch.read_xlsx(file, this.batch.params)
				})
				.then(result => {
					this.warnings = result.warnings
					this.errors = result.errors
					this.batch.sheet_data = result.sheetData
					this.batch.sheet_data_head = result.sheetDataHead
					this.batch.selected_rows = result.selectedRows
					if (result.errors.length === 0) this.mode = 'batch'
					if (this.batch.sheet_data_head.length > 0) {
						let width = 150 * (this.batch.sheet_data_head.length) + 20
						if (width < 860) width = 860
						if (width > window.outerWidth) width = window.outerWidth
						this.modal_width = width + 'px'
					}
				})
				.catch(error => {
					console.error(error)
					this.errors.push(error.message)
				})
		},
		resetBatch () {
			this.batch.file = null
			this.errors = []
			this.warnings = []
			this.success = ''
			this.batch.progress = 0
			this.batch.submitting = false
			this.batch.ext_handled = ['.XLSX', '.XLS']
			this.batch.sheet_data = []
			this.batch.sheet_data_head = []
			this.batch.selected_rows = []
			this.batch.params = []
			this.batch.filename = []
			this.mode = 'form'
			this.modal_width = '860px'
		},
		async submitBatch () {
			this.success = ''
			this.errors = []
			this.batch.progress = 0
			this.batch.submitting = true
			let submittedCount = 0
			let toSubmit = this.batch.selected_rows.length
			for (let i = 0; i < this.batch.sheet_data.length; i++) {
				if (this.batch.selected_rows.indexOf(i) !== -1 &&
					this.batch.sheet_data[i].meta.submitted === false) {
					try {
						let submitted = await this.submitJob(this.batch.sheet_data[i].params)
						// let submitted = await this.fakeSubmitJob(this.batch.sheet_data[i].params)
						this.batch.sheet_data[i].meta.submitted = submitted
						if (submitted) {
							this.batch.selected_rows.splice(this.batch.selected_rows.indexOf(i), 1)
							submittedCount++
						}
					} catch (error) {
						this.errors.push(`Job #${i} ERROR ${error}`)
					}
					this.batch.progress += 100 / toSubmit
				}
			}
			if (submittedCount === 1) {
				this.success = `1 job lancé`
			} else if (submittedCount > 1) {
				this.success = `${submittedCount} jobs lancés`
			}
			this.batch.submitting = false
		},
		submitJob (jobParams) {
			return new Promise((resolve, reject) => {
				let data = {
					jd_id: this.jd_version_selected.descriptor_id,
					jd_version: this.jd_version_selected.version_number,
					parameters: jobParams
				}
				this.$ApiMaestro.jobrequest_create(data)
					.then(response => {
						resolve(true)
					})
					.catch(error => {
						reject(error.message)
					})
			})
		},
		fakeSubmitJob (jobParams) {
			return new Promise((resolve, reject) => {
				setTimeout(() => {
					if (Math.random() > 0.5) {
						reject(new Error('error'))
					} else {
						resolve(true)
					}
				}, 1000)
			})
		},
		emitSupportPayload () {
			if (this.dialog) {
				let payload = {
					launcher: {
						mode: this.mode,
						form_component: this.form_component.initForm
					}
				}
				if (this.jds_error) {
					payload.launcher.error = this.jds_error
				}
				if (this.errors.length > 0) {
					payload.launcher.errors = this.errors.join(' - ')
				}
				if (this.warnings.length > 0) {
					payload.launcher.warnings = this.warnings.join(' - ')
				}
				if (this.jd_version_selected) {
					payload.launcher.jd = this.jd_version_selected.label
					payload.launcher.jd_id = this.jd_version_selected.descriptor_id
					payload.launcher.jd_vers_id = this.jd_version_selected.id
					payload.launcher.jd_description = this.jd_version_selected.description
				}
				if (this.forms.length === 1 && this.jd_version_selected) {
					payload.launcher_form = {}
					this.jd_version_selected.parameters.map(param => {
						let key = param.name
						key += `_[${param.type}]`
						if (param.required) key += '*'
						let value = ''
						if (this.forms[0].parameters.hasOwnProperty(param.name)) {
							value = this.forms[0].parameters[param.name]
						}
						payload.launcher_form[key] = value
					})
				}
				this.$Bus.$emit('set_support_payload', payload)
			}
		}
	},
	mounted () {
		this.$Bus.$on('get_support_payload', this.emitSupportPayload)
	},
	destroyed () {
		this.$Bus.$off('get_support_payload', this.emitSupportPayload)
	}
}
</script>

<style lang="stylus" scoped>
.prepend-label
	>>>.v-input__prepend-outer
		height: 31px
		width: 90px !important
		margin: 0
		padding: 0 10px
		align-items: center
		justify-content: flex-end

.darken-input
	>>>.v-input__slot
		background-color: var(--v-background-darken1)

>>>.v-input--checkbox
	.v-input__slot
		margin-bottom: .1em
	.v-label
		margin-bottom: 0
.v-input--switch
	margin-top: 0
	margin-bottom: .3em

.batch-table
	width: 100%
	tr:nth-child(even)
		td
			background-color: var(--v-background-lighten1)
	th
	td
		padding: .2em .5em
		max-width: 150px
		white-space: normal
		word-break: break-word
	.align-middle
		vertical-align: middle !important
	>>>.v-input--checkbox
		margin-top: 0
	>>>.v-input--selection-controls__input
		margin-right: 0
	td
		width: fit-content
		border-top: 1px solid var(--v-background-darken2)
		border-bottom: 1px solid var(--v-background-darken2)
		.cell-chip
			display: inline-block
			width: 40px !important
			height: 100%
			margin-right: 4px
			text-align: center
			background-color: rgba(0, 0, 25, 0.2)
		.v-icon
			cursor: pointer
	th
		position: sticky
		z-index: 40
		top: 0
		background-color: var(--v-background-darken2)

>>>.v-list
	background: var(--v-background-darken1) !important
	padding: 0
	.v-list__tile
		border-bottom: 1px solid var(--v-background-base)

.toggler
	border-radius: 0
	flex-wrap: wrap-reverse
	box-shadow: none
	.v-btn
		opacity: 1
		padding: 0 12px
		border-right: 2px solid var(--v-background-base)
		border-bottom: 2px solid var(--v-background-base)
		background-color: var(--v-background-darken1)

.typing-production
	border-top: 2px solid var(--v-indigo-base)
	&.v-btn--active
		background-color: var(--v-indigo-base)
		color: white !important

.typing-mixin
	border-top: 2px solid var(--v-pink-base)
	&.v-btn--active
		background-color: var(--v-pink-base)
		color: white !important

.typing-behive
	border-top: 2px solid var(--v-deep-orange-base)
	&.v-btn--active
		background-color: var(--v-deep-orange-base)
		color: white !important

.typing-suifa
	border-top: 2px solid var(--v-red-base)
	&.v-btn--active
		background-color: var(--v-red-base)
		color: white !important

.typing-audiotool
	border-top: 2px solid var(--v-lime-base)
	&.v-btn--active
		background-color: var(--v-lime-base)
		color: black !important

.typing-ingest
	border-top: 2px solid var(--v-blue-base)
	&.v-btn--active
		background-color: var(--v-blue-base)
		color: black !important

.typing-packliv
	border-top: 2px solid var(--v-amber-base)
	&.v-btn--active
		background-color: var(--v-amber-base)
		color: black !important

.typing-mam
	border-top: 2px solid var(--v-cyan-base)
	&.v-btn--active
		background-color: var(--v-cyan-base)
		color: black !important

.typing-tool
.typing-outil
	border-top: 2px solid var(--v-green-base)
	&.v-btn--active
		background-color: var(--v-green-base)
		color: white !important

.typing-qualification
	border-top: 2px solid var(--v-teal-base)
	&.v-btn--active
		background-color: var(--v-teal-base)
		color: white !important

.typing-babel
	border-top: 2px solid #ffeb3b !important
	&.v-btn--active
		background-color: #ffeb3b
		color: black !important

.typing-gandalf
	border-top: 2px solid #9e9e9e !important
	&.v-btn--active
		background-color: #9e9e9e
		color: black !important

.typing-prestation
	border-top: 2px solid #7986cb !important
	&.v-btn--active
		background-color: #7986cb
		color: black !important

</style>



// WEBPACK FOOTER //
// src/pages/maestro/launcher/launcher.vue